import React from 'react'
import { Modal } from 'antd'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import {
  TYPE_LABEL,
  releaseHeadline,
  visibleEntries,
  type EntryType,
} from '@/routes/changelog/data'

import { useChangelogModal } from './context'

// Up to this many of the shown releases' role-visible entries are featured.
const MAX_MODAL_FEATURES = 3

// Per-type accent + glyph for the feature cards (replaces the previously
// hand-coded SVGs, so the modal stays in sync with the changelog data).
const TYPE_ICON: Record<EntryType, { cls: string; paths: React.ReactNode }> = {
  feature: {
    cls: 'green',
    paths: (
      <>
        <path d="M12 3 2 8l10 5 10-5-10-5Z" />
        <path d="M2 16l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </>
    ),
  },
  improvement: {
    cls: 'blue',
    paths: (
      <>
        <path d="M18 20V10" />
        <path d="M12 20V4" />
        <path d="M6 20v-6" />
      </>
    ),
  },
  fix: {
    cls: 'orange',
    paths: (
      <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.1-.6-.6-2.1 2.3-2.3Z" />
    ),
  },
}

const COLORS = {
  azGreen100: '#d6f0e6',
  azGreen300: '#6dd2ad',
  azGreen700: '#047a48',
  azDark800: '#548052',
  azDark900: '#3e6b3d',
  orange50: '#fff7ed',
  orange100: '#ffedd5',
  orange200: '#fed7aa',
  orange500: '#f97316',
  orange600: '#ea580c',
  orange700: '#c2410c',
  blue50: '#eff6ff',
  blue700: '#1d4ed8',
  ink150: '#e7e6e3',
  ink200: '#dcdbd6',
  ink300: '#b8b6ad',
  ink500: '#65645e',
  ink700: '#353430',
  ink900: '#14130f',
  white: '#ffffff',
}

const FONT_DISPLAY = "'Bricolage Grotesque', 'DM Sans', system-ui, sans-serif"
const FONT_BODY = "'Geist', 'Inter', system-ui, sans-serif"
const FONT_MONO = "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace"

const Hero = styled.div`
  position: relative;
  font-family: ${FONT_BODY};
  background:
    radial-gradient(120% 80% at 100% 0%, rgba(249, 115, 22, 0.22) 0%, transparent 55%),
    radial-gradient(80% 100% at 0% 100%, rgba(0, 185, 107, 0.18) 0%, transparent 60%),
    linear-gradient(135deg, ${COLORS.azDark800} 0%, ${COLORS.azDark900} 100%);
  color: ${COLORS.white};
  padding: 28px 32px 26px;

  .pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: ${FONT_MONO};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #fff;
    background: rgba(255, 255, 255, 0.15);
    padding: 4px 10px;
    border-radius: 999px;
    margin-bottom: 14px;
  }
  .pill .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${COLORS.azGreen300};
  }
  h2 {
    font-family: ${FONT_DISPLAY};
    font-size: 28px;
    font-weight: 600;
    letter-spacing: -0.025em;
    color: #fff;
    margin: 0 0 6px;
  }
  p {
    font-size: 14.5px;
    color: rgba(255, 255, 255, 0.78);
    max-width: 50ch;
    margin: 0;
    line-height: 1.5;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  font-size: 18px;
  line-height: 1;
  &:hover {
    background: rgba(255, 255, 255, 0.22);
  }
`

const Body = styled.div`
  font-family: ${FONT_BODY};
  padding: 22px 24px 8px;
`

const Feat = styled.div`
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 14px;
  padding: 14px 12px;
  align-items: start;

  & + & {
    border-top: 1px solid ${COLORS.ink150};
  }

  .feat-ico {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }
  .feat-ico.green {
    background: ${COLORS.azGreen100};
    color: ${COLORS.azGreen700};
  }
  .feat-ico.orange {
    background: ${COLORS.orange100};
    color: ${COLORS.orange700};
  }
  .feat-ico.blue {
    background: ${COLORS.blue50};
    color: ${COLORS.blue700};
  }
  .feat-title {
    font-family: ${FONT_DISPLAY};
    font-size: 16px;
    font-weight: 600;
    color: ${COLORS.ink900};
    margin-bottom: 2px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .feat-title .new {
    font-family: ${FONT_MONO};
    font-size: 10px;
    font-weight: 700;
    color: ${COLORS.orange700};
    background: ${COLORS.orange50};
    border: 1px solid ${COLORS.orange200};
    padding: 1px 6px;
    border-radius: 999px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .feat-text {
    font-size: 13.5px;
    color: ${COLORS.ink500};
    line-height: 1.5;
  }
`

const MoreLine = styled.p`
  font-family: ${FONT_BODY};
  font-size: 13px;
  color: ${COLORS.ink500};
  margin: 4px 12px 0;
`

const Foot = styled.div`
  font-family: ${FONT_BODY};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px 22px;
  gap: 12px;

  .skip {
    background: transparent;
    border: 0;
    color: ${COLORS.ink500};
    font-size: 13.5px;
    cursor: pointer;
    &:hover {
      color: ${COLORS.ink700};
    }
  }
  .cta-row {
    display: flex;
    gap: 10px;
  }
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    border: 1px solid transparent;
    font-family: inherit;
  }
  .btn.ghost {
    background: transparent;
    color: ${COLORS.ink700};
    border-color: ${COLORS.ink200};
    &:hover {
      background: #fff;
      border-color: ${COLORS.ink300};
    }
  }
  .btn.primary {
    background: ${COLORS.orange500};
    color: #fff;
    box-shadow: 0 4px 14px rgba(249, 115, 22, 0.3);
    &:hover {
      background: ${COLORS.orange600};
    }
  }
`

export const WelcomeModal: React.FC = () => {
  const { open, closeModal, shownReleases, role } = useChangelogModal()
  const navigate = useNavigate()

  // Newest first. Only what this account's role is concerned by, across every
  // release it has not been shown since its last visit.
  const newest = shownReleases[0]
  const oldest = shownReleases[shownReleases.length - 1]
  const all = shownReleases.flatMap((release) =>
    visibleEntries(release, role).map((entry) => ({ release, entry })),
  )
  const features = all.slice(0, MAX_MODAL_FEATURES)
  const more = all.length - features.length

  const headline =
    shownReleases.length === 1 && newest
      ? releaseHeadline(newest, role)
      : {
          title: 'Ce qui a changé depuis votre dernière visite',
          summary: `${all.length} nouveautés vous concernent, sur ${shownReleases.length} mises à jour.`,
        }

  const goToChangelog = () => {
    closeModal()
    navigate('/changelog')
  }

  return (
    <Modal
      open={open}
      onCancel={closeModal}
      footer={null}
      closable={false}
      centered
      width={640}
      styles={{
        content: { padding: 0, overflow: 'hidden', borderRadius: 20 },
        body: { padding: 0 },
      }}
      aria-labelledby="welcome-title"
    >
      <Hero>
        {newest && (
          <span className="pill">
            <span className="dot" /> Mise à jour ·{' '}
            {oldest && oldest !== newest
              ? `v${oldest.version} → v${newest.version}`
              : `v${newest.version}`}{' '}
            · {newest.date}
          </span>
        )}
        <h2 id="welcome-title">{headline.title}</h2>
        {headline.summary && <p>{headline.summary}</p>}
        <CloseButton
          onClick={closeModal}
          aria-label="Fermer"
          data-cy="changelog-modal-close"
        >
          ×
        </CloseButton>
      </Hero>

      <Body>
        {features.map(({ release, entry }, i) => {
          const icon = TYPE_ICON[entry.type]
          return (
            <Feat key={`${release.version}-${i}`} data-cy="changelog-modal-entry">
              <div className={`feat-ico ${icon.cls}`}>
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {icon.paths}
                </svg>
              </div>
              <div>
                <div className="feat-title">
                  {entry.title}{' '}
                  <span className="new">{TYPE_LABEL[entry.type]}</span>
                </div>
                <div className="feat-text">{entry.body}</div>
              </div>
            </Feat>
          )
        })}
        {more > 0 && (
          <MoreLine>
            … et {more} autre{more > 1 ? 's' : ''} dans « Tout le détail ».
          </MoreLine>
        )}
      </Body>

      <Foot>
        <button type="button" className="skip" onClick={closeModal}>
          Plus tard
        </button>
        <div className="cta-row">
          <button type="button" className="btn ghost" onClick={goToChangelog}>
            Tout le détail
          </button>
          <button type="button" className="btn primary" onClick={closeModal}>
            Commencer
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            >
              <path d="M5 12h14" />
              <path d="M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      </Foot>
    </Modal>
  )
}
